/**
 * Tag-value display names over the regional eval API (issue #1410).
 *
 * Tags only exist in this region's `usage_logs`, so their names live beside
 * them rather than in the Global Layer — the same service the Insights page
 * already asks for every aggregate, metadata key and criterion.
 *
 * The transport carries the Cognito bearer token and `X-Impersonate-Domain`
 * already, so the server resolves the workspace; `domain` is never sent. It
 * also refuses writes while impersonating, which is why the UI hides its rename
 * controls in that state rather than letting a request fail.
 */
import { makeApiRequestPromise } from "../../lib/api";

const PATH = "/api/v1/tag-aliases";

/** The whole workspace map, `tag value → display name`. One request serves
 *  every render site. */
export async function fetchTagAliases(): Promise<Record<string, string>> {
  const response = await makeApiRequestPromise({ path: PATH, method: "GET" });
  return response?.aliases ?? {};
}

export async function putTagAlias(
  value: string,
  displayName: string,
): Promise<void> {
  await makeApiRequestPromise({
    path: PATH,
    method: "PUT",
    body: { value, display_name: displayName },
  });
}

/** Drop a name, so the value renders raw again. The value travels as a query
 *  parameter: it is customer-supplied and may contain a slash or be the empty
 *  string, neither of which survives a path segment. */
export async function deleteTagAlias(value: string): Promise<void> {
  await makeApiRequestPromise({
    path: PATH,
    method: "DELETE",
    params: { value },
  });
}
